// Модалка «Сообщения» — притворяется чатом с разработчиком,
// но на самом деле показывает последние коммиты git как changelog.
//
// Источник — define __GIT_LOG__ из vite.config.ts (читается на этапе старта
// dev-сервера или build, формат "hash|subject|iso-date").
// HMR не пересобирает define — после нового коммита перезапусти Vite,
// чтобы увидеть свежий лог.

import { useEffect, useState } from 'react';
import { deleteMessage, deleteAllMessages, respondFriendRequest,
  type ServerMessage, type ChangelogCommit, type FriendRespondAction } from '../api/client';
import { useTr } from '../i18n/useTr';
import styles from './MessagesModal.module.css';

interface Commit {
  hash: string;
  subject: string;
  date: Date;
}

// Формат: "15:45 14/05/26 UTC" — единый стиль для DEV-сообщений и
// серверных уведомлений. UTC чтобы не путать игроков из разных таймзон.
function formatDate(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())} ${pad(d.getUTCDate())}/${pad(d.getUTCMonth() + 1)}/${String(d.getUTCFullYear()).slice(-2)} UTC`;
}

// Префикс «Этап N.M: ...» отрезаем чтобы заголовок коммита читался чище.
// Иначе всё одинаково начинается «Этап ...» — глаза устают.
function cleanSubject(s: string): { stage: string | null; text: string } {
  const m = s.match(/^Этап\s+([\d.]+)(?:\s*[:—-]\s*|\s+)(.+)$/);
  if (m) return { stage: m[1], text: m[2] };
  return { stage: null, text: s };
}

interface Props {
  onClose: () => void;
  serverMessages?: ServerMessage[];
  commits?: ChangelogCommit[];          // МСГ: серверный changelog
  onHideCommit?: (hash: string) => void; // скрыть один коммит локально
  onHideAllCommits?: () => void;         // скрыть все коммиты
  onMessagesChanged?: () => void; // вызывается после удаления сообщений
}

const CATEGORY_ICON: Record<string, string> = {
  grant:     '🎁',
  reward:    '🎁',
  ban:       '🔒',
  freeze:    '❄',
  unfreeze:  '✓',
  support:   '✉',
  system:    '⚙',
  match_win:  '🏆',
  match_loss: '💀',
  match_draw: '⚖',
  achievement: '🏅'
};

export function MessagesModal({
  onClose, serverMessages = [],
  commits: serverCommits = [],
  onHideCommit, onHideAllCommits,
  onMessagesChanged
}: Props) {
  const tr = useTr();
  const isEn = tr('ru', 'en') === 'en';
  // Парсим коммиты с сервера в локальный формат с Date-объектом.
  const commits: Commit[] = serverCommits.map(c => ({
    hash: c.hash, subject: c.subject, date: new Date(c.iso_date)
  }));
  // Локально скрытые серверные сообщения (оптимистично до refresh).
  const [hidden, setHidden] = useState<Set<number>>(new Set());
  // ДРУГ.2: оптимистично скрытые кнопки на сообщениях после нажатия.
  // Чтобы не дёргать кнопки повторно пока летит запрос на сервер.
  const [actionTaken, setActionTaken] = useState<Set<number>>(new Set());
  // Подсветить статус ответа на friend-request (показывается на 2 сек).
  const [actionResult, setActionResult] = useState<Map<number, string>>(new Map());

  // Обработка нажатия на кнопку friend_*. mapping action.type → respond action.
  const onMessageAction = async (msg: ServerMessage, actionType: string, payload: any) => {
    const friendActionMap: Record<string, FriendRespondAction | undefined> = {
      'friend_accept':          'accept',
      'friend_decline_temp':    'decline_temp',
      'friend_decline_forever': 'decline_forever'
    };
    const fa = friendActionMap[actionType];
    if (!fa || typeof payload?.requestId !== 'number') return;
    setActionTaken(prev => new Set([...prev, msg.id]));
    try {
      await respondFriendRequest(payload.requestId, fa);
      const label = fa === 'accept' ? tr('✓ Принято', '✓ Accepted')
                  : fa === 'decline_temp' ? tr('✗ Отклонено', '✗ Declined')
                  : tr('⛔ Заблокировано', '⛔ Blocked');
      setActionResult(prev => new Map(prev).set(msg.id, label));
      onMessagesChanged?.();
    } catch (e: any) {
      // Откатываем чтобы юзер мог попробовать снова.
      setActionTaken(prev => {
        const next = new Set(prev);
        next.delete(msg.id);
        return next;
      });
      setActionResult(prev => new Map(prev).set(msg.id, tr('⚠ Ошибка', '⚠ Error')));
    }
  };

  // МСГ: убрали window.confirm — удаление мгновенное по запросу юзера.
  const onDeleteOne = async (id: number) => {
    setHidden(prev => new Set([...prev, id]));
    try {
      await deleteMessage(id);
      onMessagesChanged?.();
    } catch {
      setHidden(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const onClearAll = async () => {
    // МСГ: убрали window.confirm.
    setHidden(prev => new Set([...prev, ...serverMessages.map(m => m.id)]));
    try {
      await deleteAllMessages();
      onMessagesChanged?.();
    } catch {
      setHidden(new Set());
    }
    // Также скрываем все DEV-коммиты в текущем списке.
    onHideAllCommits?.();
  };

  const visibleMessages = serverMessages.filter(m => !hidden.has(m.id));

  // Esc закрывает модалку
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <div className={styles.devDot} />
            <div>
              <div className={`${styles.title} cd`}>DEV CHANNEL</div>
              <div className={`${styles.subtitle} cm`}>incoming · ENC-1990</div>
            </div>
          </div>
          <button className={styles.closeBtn} onClick={onClose} aria-label={tr('Закрыть', 'Close')}>
            ×
          </button>
        </div>

        <div className={styles.scanLine} />

        <div className={styles.feed}>
          {(visibleMessages.length > 0 || commits.length > 0) && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '4px 0' }}>
              <button
                onClick={onClearAll}
                style={{
                  background: 'transparent',
                  border: '0.5px solid #ff7a7a',
                  color: '#ff7a7a',
                  fontSize: 10,
                  padding: '4px 10px',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  letterSpacing: 0.5
                }}
              >
                {tr('ОЧИСТИТЬ ВСЁ', 'CLEAR ALL')}
              </button>
            </div>
          )}
          {/* Сначала — сообщения от админа/системы (новые сверху). */}
          {visibleMessages.map((m) => {
            const date = new Date(m.createdAt);
            const isUnread = m.readAt === null;
            const icon = (m.category && CATEGORY_ICON[m.category]) || (m.kind === 'admin' ? '✉' : '⚙');
            return (
              <div key={`s${m.id}`} className={`${styles.message} ${isUnread ? styles.latest : ''}`}>
                <div className={styles.msgHeader}>
                  <div className={styles.msgFrom}>
                    {isUnread && <span className={styles.newBadge}>NEW</span>}
                    <span className="cd">{icon} {m.kind === 'admin' ? tr('АДМИНИСТРАТОР', 'ADMIN') : tr('СИСТЕМА', 'SYSTEM')}</span>
                  </div>
                  <div className={styles.msgMeta}>
                    <span className={styles.date}>{formatDate(date)}</span>
                    <button
                      onClick={() => onDeleteOne(m.id)}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: 'var(--t-lo)',
                        cursor: 'pointer',
                        fontSize: 14,
                        marginLeft: 6,
                        padding: '0 4px'
                      }}
                      title={tr('Удалить сообщение', 'Delete message')}
                    >×</button>
                  </div>
                </div>
                <div className={styles.msgBody}>
                  <strong style={{ color: 'var(--t-hi)', display: 'block', marginBottom: 4 }}>{m.title}</strong>
                  {m.body}
                  {/* ДРУГ.2: интерактивные кнопки (например для friend_request).
                      Если actionTakenAt уже стоит на сервере — кнопки не пришли.
                      Если только что нажали локально — показываем результат. */}
                  {Array.isArray(m.actions) && m.actions.length > 0 && !m.actionTakenAt && !actionTaken.has(m.id) && (
                    <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
                      {m.actions.map((a, ai) => {
                        const isAccept = a.type === 'friend_accept';
                        const isForever = a.type === 'friend_decline_forever';
                        return (
                          <button
                            key={ai}
                            onClick={() => onMessageAction(m, a.type, a.payload)}
                            style={{
                              background: isAccept ? 'var(--neon-li, #b4ff00)' : 'transparent',
                              color: isAccept ? '#06131A' : isForever ? '#ff7a7a' : 'var(--t-hi)',
                              border: isAccept ? 'none' : `0.5px solid ${isForever ? '#ff7a7a' : 'var(--line, #555)'}`,
                              padding: '7px 12px',
                              fontFamily: 'inherit',
                              fontSize: 11,
                              fontWeight: 600,
                              letterSpacing: 0.4,
                              textTransform: 'uppercase',
                              cursor: 'pointer'
                            }}
                          >{a.label}</button>
                        );
                      })}
                    </div>
                  )}
                  {actionResult.has(m.id) && (
                    <div style={{ marginTop: 8, fontSize: 11, color: 'var(--t-mute, #888)', fontStyle: 'italic' }}>
                      {actionResult.get(m.id)}
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {/* Затем — git changelog от разработки. */}
          {commits.length === 0 && visibleMessages.length === 0 && (
            <div className={styles.empty}>
              <div className="cd" style={{ color: 'var(--neon-am)', fontSize: 11 }}>
                NO TRANSMISSION
              </div>
              <div className="cb" style={{ color: 'var(--t-lo)', fontSize: 11, marginTop: 6 }}>
                {tr('Канал тих. Жди патчей от разработки.', 'Channel silent. Wait for dev patches.')}
              </div>
            </div>
          )}
          {commits.length > 0 && visibleMessages.length > 0 && (
            <div style={{
              padding: '8px 0',
              borderTop: '1px solid rgba(255,61,143,0.20)',
              marginTop: 8,
              textAlign: 'center'
            }}>
              <span className="cd" style={{ color: 'var(--t-mute)', fontSize: 9, letterSpacing: '0.15em' }}>
                ── DEV CHANGELOG ──
              </span>
            </div>
          )}
          {commits.map((c, i) => {
            const { stage, text } = cleanSubject(c.subject);
            const isLatest = i === 0;
            return (
              <div key={c.hash} className={`${styles.message} ${isLatest ? styles.latest : ''}`}>
                <div className={styles.msgHeader}>
                  <div className={styles.msgFrom}>
                    {isLatest && <span className={styles.newBadge}>NEW</span>}
                    <span className="cd">DEV</span>
                    {stage && <span className={styles.stage}>#{stage}</span>}
                  </div>
                  <div className={styles.msgMeta}>
                    <span className={styles.hash}>{c.hash}</span>
                    <span className={styles.date}>{formatDate(c.date)}</span>
                    {onHideCommit && (
                      <button
                        onClick={() => onHideCommit(c.hash)}
                        style={{
                          background: 'transparent', border: 'none',
                          color: 'var(--t-lo)', cursor: 'pointer',
                          fontSize: 14, marginLeft: 6, padding: '0 4px'
                        }}
                        title={tr('Скрыть', 'Hide')}
                      >×</button>
                    )}
                  </div>
                </div>
                <div className={styles.msgBody}>{text}</div>
              </div>
            );
          })}
        </div>

        <div className={styles.footer}>
          <span className="cm">
            {visibleMessages.length > 0 && `${visibleMessages.length} ${isEn ? (visibleMessages.length === 1 ? 'msg' : 'msgs') : 'сообщ.'} · `}
            {commits.length} {isEn
              ? (commits.length === 1 ? 'commit' : 'commits')
              : (commits.length === 1 ? 'коммит' : commits.length < 5 ? 'коммита' : 'коммитов')}
            {' · build '}{formatDate(new Date(__BUILD_DATE__))}
          </span>
        </div>
      </div>
    </div>
  );
}
